'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import type { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: (request: Parameters<typeof newsletterSubscribe>[0]) =>
      import('@eliadmin/core/src/api/newsletter/newsletter-api')
        .then(api => api.newsletterSubscribe(request)),
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return !isSubscribed ? (
    <form
      className={cn('grid grid-cols-12 gap-6', 'ea-homepage-newsletter__form')}
      onSubmit={handleSubmit}
    >
      <div
        className={cn(
          'col-span-12 flex w-full flex-col items-start justify-start gap-6 md:col-span-7 md:max-w-md',
          'ea-homepage-newsletter__form-icon-email',
        )}
      >
        <h2
          className={cn(
            'text-left text-3xl font-bold text-background',
            'ea-homepage-newsletter__form-icon-email-title',
          )}
        >
          {t('text574') != 'text574' ? t('text574') : 'Odoberanie noviniek e-mailom'}
          {/* TODO: preklad */}
          {/* {t('text184')} */}
        </h2>
        <p className="text-background">
          {/* TODO: preklad */}
          {t('text575') != 'text575'
            ? t('text575')
            : 'Chcete byť informovaní o akciách a zľavách medzi prvými? Stačí zadať e-mailovú adresu.'}
        </p>
      </div>
      <div
        className={cn(
          'col-span-12 flex flex-col items-start justify-center gap-2 md:col-span-5',
          'ea-homepage-newsletter__form-item-email',
        )}
      >
        <div className={cn('flex w-full flex-row items-center')}>
          <Input
            aria-label={systemLabels('email')}
            id="newsletter-email"
            type="email"
            name="email"
            placeholder={t('text183')}
            onChange={e => setEmail(e.target.value)}
            rounded="none"
            className={{
              wrapper: cn('rounded-s-full text-foreground'),
            }}
          />
          <Button
            type="submit"
            className={cn(
              'rounded-s-none px-4',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
          >
            {t('text182')}
          </Button>
        </div>
        <div
          className={cn(
            'flex w-full flex-row items-center gap-2',
            'ea-homepage-newsletter__form-item-gdpr',
          )}
        >
          <span className="relative h-5 w-5 shrink-0">
            <input
              type="checkbox"
              checked={checked}
              onChange={event => setChecked(event.target.checked)}
              id="newsletter-gdpr"
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              required
              className="peer block h-5 w-5 appearance-none rounded-[4px] border bg-background checked:bg-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <svg
              aria-hidden="true"
              viewBox="0 0 15 15"
              fill="none"
              className="pointer-events-none absolute left-0.5 top-0.5 hidden h-4 w-4 text-primary-foreground peer-checked:block"
            >
              <path
                d="M11.4669 3.72684C11.6868 3.87443 11.7455 4.1727 11.5979 4.39256L6.76459 11.5926C6.6838 11.7129 6.55277 11.7899 6.40842 11.8021C6.26407 11.8143 6.12191 11.7603 6.02191 11.6563L3.38857 8.91631C3.20464 8.72493 3.21069 8.42019 3.40207 8.23626C3.59345 8.05233 3.89819 8.05838 4.08212 8.24976L6.3035 10.561L10.8012 3.85783C10.9488 3.63797 11.247 3.57925 11.4669 3.72684Z"
                fill="currentColor"
                fillRule="evenodd"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <Label htmlFor="newsletter-gdpr">
            <span>
              {t('text264')}&nbsp;
              <a href={t('text391')} className={cn('underline')}>
                {t('text265')}
              </a>{' '}
              <a href={t('text392')} className={cn('underline')}>
                {t('text436')}
              </a>
              .
            </span>
          </Label>
        </div>
      </div>
    </form>
  ) : (
    <div className={cn('flex flex-col items-center justify-center gap-2')}>
      <span className="text-2xl font-bold">
        {t('text576') != 'text576'
          ? t('text576')
          : 'Ďakujeme za prihlásenie do newslettera. Na e-mailovú adresu vám bolo zaslané potvrdenie prihlásenia.'}
      </span>
    </div>
  );
}
